import { useEffect, useState } from 'react';

import type { UspDetailPopupItem } from '@/components/shared/einstein/usp-popup';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Image } from '@/components/shared/media/image';
import { generateBannerImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { keyframes, styled } from '@/stitches.config';

export interface UspPopupFooterCustomizeItem {
  image: ImageProps;
  title: string;
}
interface UspPopupFooterProps {
  item: UspDetailPopupItem;
  parentItem?: UspDetailPopupItem;
  className: string;
  onNext: () => void;
  customizeItem?: UspPopupFooterCustomizeItem;
}

const hoverIconAnimation = keyframes({
  '0%': {
    transform: 'translateX(0px)',
  },
  '25%': {
    transform: 'translateX(-10px)',
  },
  '100%': {
    transform: 'translateX(10px)',
  },
});

const StyledTextWrapper = styled(Box, {
  position: 'relative',
  zIndex: '$1',
  height: '100%',
  paddingTop: '60px',
  '@lg': {
    paddingTop: '84px',
  },
  '& .next-icon': {
    transition: 'opacity 0ms',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(90deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 50%)',
    zIndex: '-1',
    transition: 'opacity 800ms cubic-bezier(0.17, 0.84, 0.44, 1)',
    willChange: 'opacity',
    pointerEvents: 'none',
  },
  '@hover': {
    '&:hover': {
      '&::before': {
        opacity: 0.4,
      },
    },
  },
});

const AnimationFooterLeave = keyframes({
  '100%': { height: 'calc(var(--next-height, 100vh) * 1px)' },
});

const StyledFooterSizeWrapper = styled(Box, {
  position: 'relative',
  width: '$size-full',
  height: 'calc($size-48 + $size-1)', // progress bar height sticky
  cursor: 'pointer',
  '@lg': {
    height: 'calc($size-60 + $size-1)', // progress bar height sticky
  },
});

const StyledUspPopupFooter = styled(Box, {
  position: 'relative',
  bottom: 0,
  left: 0,
  width: '$size-full',
  height: '$size-full',
  backgroundColor: '$black',
  [`${StyledTextWrapper}`]: {
    '.lead': {
      opacity: 0,
    },
  },
  '&.leave': {
    zIndex: '$modal',
    pe: 'none !important',
    position: 'sticky',
    borderTopLeftRadius: '$rounded-4',
    borderTopRightRadius: '$rounded-4',
    overflow: 'hidden',
    [`${StyledFooterSizeWrapper}`]: {
      willChange: 'height',
      animation: `${AnimationFooterLeave} 800ms cubic-bezier(0.17,0.84,0.44,1)`,
    },
    [`${StyledTextWrapper}`]: {
      justifyContent: 'space-between',
      '&::before': {
        transition: 'none',
        visibilty: 'hidden',
        opacity: 0,
      },
      '.next-text,.next-icon': {
        opacity: 0,
      },
      '.lead': {
        opacity: 1,
      },
      paddingTop: '44px',
      '@lg': {
        paddingTop: '68px',
      },
    },
    '.image': {
      '&::before': {
        opacity: 0.8,
      },
      '&::after': {
        opacity: 1,
      },
    },
  },

  '&:hover': {
    '@lg': {
      '@landscape': {
        [`${StyledTextWrapper}`]: {
          '& .next-icon': {
            animation: `1s ease 0s infinite normal none running ${hoverIconAnimation}`,
          },
        },
      },
    },
  },
});

export const UspPopupFooter = ({
  item,
  parentItem,
  customizeItem,
  className,
  onNext,
}: UspPopupFooterProps) => {
  const [initHeight, setInitHeight] = useState<number>(0);
  useEffect(() => {
    if (initHeight === 0) {
      setInitHeight(window.innerHeight);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledUspPopupFooter className={className}>
      <StyledFooterSizeWrapper
        css={{
          '--next-height': initHeight - 40,
          '@2xl': {
            '--next-height': initHeight - 80,
          },
        }}
        onClick={onNext}
        tracking={{
          dataTrack: 'usp_footer_next',
          dataTrackSection: parentItem?.menu,
          dataTrackValue: item?.menu,
          dataTrackText: `ถัดไป ${item?.title}`,
        }}
        test={{
          dataTest: 'usp-footer-next',
        }}
      >
        {item ? (
          <Image
            src={item.headerImage.desktop.src}
            alt={item.headerImage.desktop.alt}
            width={item.headerImage.desktop.width}
            height={item.headerImage.desktop.height}
            className="image"
            hoverEffect="zoom"
            layout="fill"
            sources={generateBannerImageSources({
              mobileSrc: item.headerImage.mobile.src,
              mobileOptions: {
                crop: '9:16',
              },
              desktopSrc: item.headerImage.desktop.src,
              desktopOptions: {
                crop: '16:9',
              },
            })}
            css={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                width: '100%',
                opacity: 0,
                left: 0,
                transition: 'opacity ease 800ms',
              },
              '&::before': {
                top: '$space-0',
                height: 245,
                background: `linear-gradient(180deg, ${parentItem?.cardColor} 0%, rgba(0, 0, 0, 0) 100%)`,
                zIndex: '$1',
              },
              '&::after': {
                bottom: '-$space-5',
                height: 230,
                background:
                  'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)',
                '@2xl': {
                  height: 210,
                  bottom: '-$space-10',
                },
              },
              img: {
                minHeight: 'calc(100% + 40px)',
                '@2xl': {
                  minHeight: 'calc(100% + 80px)',
                },
              },
            }}
            data-test="usp_footer_image"
          />
        ) : (
          customizeItem && (
            <Box
              position="absolute"
              width="full"
              height="full"
              css={{
                background:
                  'linear-gradient(rgb(53, 52, 52) 0%, rgba(43, 43, 43, 0) 68.72%, rgb(53, 52, 52) 100%), linear-gradient(rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.7) 105.11%, rgba(255, 255, 255, 0.42) 111.44%, rgba(255, 255, 255, 0) 153.67%), linear-gradient(0deg, rgb(79, 79, 79), rgb(79, 79, 79))',
                overflow: 'hidden',
                '&::before': {
                  content: '',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '$size-full',
                  height: '$size-full',
                  background:
                    'linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 50%)',
                },
              }}
              test={{
                dataTest: 'usp_footer_customize',
              }}
            >
              <Container
                maxWidth="none"
                css={{
                  height: '$size-full',
                }}
              >
                <Container
                  size="none"
                  maxWidth={{ '@initial': 'xs', '@lg': 'none' }}
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    position: 'relative',
                    height: '$size-full',
                    '@2xl': { px: '$space-16' },
                  }}
                >
                  <Image
                    src={customizeItem.image.src}
                    alt={customizeItem.image.alt}
                    width={customizeItem.image.width}
                    height={customizeItem.image.height}
                    layout="fixed"
                    sizes="(max-width: 1023px) 260px, 340px"
                    useSrcSetPattern={true}
                    objectFit="contain"
                    srcSetOptions={{
                      crop: '76p,83p',
                    }}
                    css={{
                      width: '260px',
                      transform: 'translateX(145px)',
                      '@sm': {
                        transform: 'translateX(-35px)',
                      },
                      '@lg': {
                        transform: 'translateX(-75px)',
                        width: '340px',
                      },
                    }}
                  />
                </Container>
              </Container>
            </Box>
          )
        )}
        <StyledTextWrapper>
          <Container maxWidth="none">
            <Container
              size="none"
              maxWidth={{ '@initial': 'xs', '@lg': 'none' }}
              css={{
                '@2xl': { px: '$space-16' },
              }}
            >
              <Text
                variant="text-disabled"
                size="text-body-1"
                css={{
                  marginBottom: '$space-1',
                  transition: `opacity 800ms cubic-bezier(0.17,0.84,0.44,1)`,
                  willChange: 'opacity',
                }}
                className="next-text"
              >
                ถัดไป
              </Text>
              <Box
                display="flex"
                columnGap="4"
                justifyContent="between"
                alignItems="center"
              >
                {item ? (
                  <Text
                    size="text-h2"
                    variant="text-white"
                    dangerouslySetInnerHTML={{
                      __html: item?.title,
                    }}
                  />
                ) : (
                  customizeItem && (
                    <Text
                      size="text-h2"
                      variant="text-white"
                      dangerouslySetInnerHTML={{
                        __html: customizeItem?.title,
                      }}
                    />
                  )
                )}

                <Icon
                  icon="arrow-forward"
                  color="icon-white"
                  className="next-icon"
                  size={{
                    '@initial': 'lg',
                    '@lg': '3xl',
                  }}
                />
              </Box>
            </Container>
          </Container>
        </StyledTextWrapper>
      </StyledFooterSizeWrapper>
    </StyledUspPopupFooter>
  );
};

UspPopupFooter.displayName = 'SharedUspPopupFooter';
